import { Grid, OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { FC, useMemo } from "react";
import { BeamReinforcementResult } from "../../../http/beamReinforcement/formulas";
import { registerCapturableCanvas } from "../../../utils/webglCanvases";
import styles from "./Beam3DDrawing.module.css";

type Beam3DDrawingProps = {
  result: BeamReinforcementResult;
  existingLength: number; // m
  reinforcementLength: number; // m
};

const EXISTING_COLOR = "#efd8a8";
const REINFORCEMENT_COLOR = "#e0c799";
const OK_COLOR = "#5f8f3a";
const BAD_COLOR = "#e3002b";

const MM_TO_M = 1 / 1000;

type BoxSpec = { key: string; position: [number, number, number]; size: [number, number, number]; color: string };

const Beam3DDrawing: FC<Beam3DDrawingProps> = ({ result, existingLength, reinforcementLength }) => {
  const { b1, h1, b2, h2, n1, screw, screwOK, screwSpacing } = result;

  const b1m = b1 * MM_TO_M;
  const h1m = h1 * MM_TO_M;
  const b2m = b2 * MM_TO_M;
  const h2m = h2 * MM_TO_M;
  const screwSpacingM = screwSpacing * MM_TO_M;
  const screwRadiusM = (screw.screwFamily.diameter / 2) * MM_TO_M;

  const totalZ = n1 === 2 ? b2m + b1m + b2m : b1m + b2m;
  const centerOffset = totalZ / 2;

  const boxes = useMemo<BoxSpec[]>(() => {
    if (n1 === 2) {
      return [
        {
          key: "reinforcement-a",
          position: [0, 0, b2m / 2 - centerOffset],
          size: [reinforcementLength, h2m, b2m],
          color: REINFORCEMENT_COLOR,
        },
        {
          key: "existing",
          position: [0, 0, b2m + b1m / 2 - centerOffset],
          size: [existingLength, h1m, b1m],
          color: EXISTING_COLOR,
        },
        {
          key: "reinforcement-b",
          position: [0, 0, b2m + b1m + b2m / 2 - centerOffset],
          size: [reinforcementLength, h2m, b2m],
          color: REINFORCEMENT_COLOR,
        },
      ];
    }

    return [
      {
        key: "existing",
        position: [0, 0, b1m / 2 - centerOffset],
        size: [existingLength, h1m, b1m],
        color: EXISTING_COLOR,
      },
      {
        key: "reinforcement",
        position: [0, 0, b1m + b2m / 2 - centerOffset],
        size: [reinforcementLength, h2m, b2m],
        color: REINFORCEMENT_COLOR,
      },
    ];
  }, [n1, b1m, h1m, b2m, h2m, existingLength, reinforcementLength, centerOffset]);

  const interfaceZs = n1 === 2 ? [b2m - centerOffset, b2m + b1m - centerOffset] : [b1m - centerOffset];

  const screwPositions = useMemo<Array<[number, number, number]>>(() => {
    const overlap = Math.min(existingLength, reinforcementLength);
    if (overlap <= 0 || screwSpacingM <= 0) return [];

    const positions: Array<[number, number, number]> = [];
    for (let x = -overlap / 2; x <= overlap / 2 + 1e-6; x += screwSpacingM) {
      for (const z of interfaceZs) {
        positions.push([x, 0, z]);
      }
    }
    return positions;
  }, [existingLength, reinforcementLength, screwSpacingM, interfaceZs]);

  const maxDim = Math.max(existingLength, reinforcementLength, totalZ, h1m, h2m, 0.5);
  const screwColor = screwOK ? OK_COLOR : BAD_COLOR;
  const thickestBeamHalfHeight = Math.max(h1m, h2m) / 2;

  return (
    <div className={styles.canvasWrapper}>
      <Canvas
        camera={{ position: [maxDim * 1.1, maxDim * 0.85, maxDim * 1.3], fov: 40 }}
        onCreated={registerCapturableCanvas}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[maxDim, maxDim * 2, maxDim]} intensity={1.1} />
        <directionalLight position={[-maxDim, maxDim, -maxDim]} intensity={0.3} />

        {boxes.map((box) => (
          <mesh key={box.key} position={box.position}>
            <boxGeometry args={box.size} />
            <meshStandardMaterial color={box.color} />
          </mesh>
        ))}

        {screwPositions.map((position, index) => (
          <mesh key={index} position={position} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[screwRadiusM, screwRadiusM, Math.max(b1m, b2m) * 1.05, 12]} />
            <meshStandardMaterial color={screwColor} />
          </mesh>
        ))}

        <Grid
          position={[0, -thickestBeamHalfHeight - 0.02, 0]}
          args={[maxDim * 3, maxDim * 3]}
          cellSize={maxDim / 10}
          sectionSize={maxDim}
          fadeDistance={maxDim * 6}
          infiniteGrid
        />

        <OrbitControls makeDefault target={[0, 0, 0]} />
      </Canvas>
    </div>
  );
};

export default Beam3DDrawing;
